import { resolveMockupLayerOrder } from '@designdeck/shared';
import type { MockupBlendMode } from '@/api/products';
import type { UIProductMockupTemplate } from '@/lib/product-transformer';
import { HEATHER_PATTERN_FALLBACK } from '@/utils/mockupLayers';
import { toAlphaMaskUrl } from '@/lib/alphaMask';

/**
 * The product with the design on it, as one image.
 *
 * MockupRenderer shows that stack with DOM layers, CSS blend modes and CSS
 * masks — nothing a customer could save or send. This draws the same stack
 * onto a canvas: same layer order (resolveMockupLayerOrder, embroidery
 * exception included), same blend modes and opacities, the same alpha mask
 * the renderer uses, the cord layer and the overlay above the mask.
 *
 * What is drawn is decided by planMockupLayers, which is pure and tested;
 * composeMockupImage only loads the images and follows the plan.
 */

export interface MockupCompositeInput {
  template: UIProductMockupTemplate;
  /** Garment colour behind the cutout mask. */
  backgroundColor: string;
  isHeathered?: boolean;
  isEmbroidery?: boolean;
  /** The design as exported from the print area; null for a blank product. */
  designUrl: string | null;
  /** Ground behind the product. */
  frameBackground?: string;
  /** Longest edge of the result; 1200 px by default. */
  maxEdge?: number;
}

export type CompositeOperation = GlobalCompositeOperation;

export type MockupLayerStep =
  | {
      kind: 'image';
      layer: 'heathered' | 'base' | 'texture' | 'shadow';
      url: string;
      operation: CompositeOperation;
      opacity: number;
    }
  | { kind: 'design'; url: string };

const OPERATIONS: Record<MockupBlendMode, CompositeOperation> = {
  NORMAL: 'source-over',
  MULTIPLY: 'multiply',
  SCREEN: 'screen',
  OVERLAY: 'overlay',
  SOFT_LIGHT: 'soft-light',
  HARD_LIGHT: 'hard-light',
};

const operationFor = (mode?: MockupBlendMode | null): CompositeOperation =>
  (mode && OPERATIONS[mode]) || 'source-over';

/** The masked product stack, bottom to top — what MockupRenderer renders. */
export function planMockupLayers(
  input: Pick<
    MockupCompositeInput,
    'template' | 'isHeathered' | 'isEmbroidery' | 'designUrl'
  >
): MockupLayerStep[] {
  const { template, isHeathered = false, isEmbroidery = false } = input;
  const steps: MockupLayerStep[] = [];

  for (const layer of resolveMockupLayerOrder(template.layerOrder, {
    shadingBelowDesign: isEmbroidery,
  })) {
    if (layer === 'heathered' && isHeathered) {
      steps.push({
        kind: 'image',
        layer: 'heathered',
        url: template.heatheredOverlayUrl || HEATHER_PATTERN_FALLBACK,
        operation: operationFor(template.heatheredBlendMode),
        opacity: template.heatheredOpacity ?? 1,
      });
    } else if (layer === 'design' && input.designUrl) {
      steps.push({ kind: 'design', url: input.designUrl });
    } else if (layer === 'base') {
      steps.push({
        kind: 'image',
        layer: 'base',
        url: template.imageUrl,
        operation: 'source-over',
        opacity: template.baseOpacity ?? 1,
      });
    } else if (layer === 'texture' && template.textureOverlayUrl) {
      steps.push({
        kind: 'image',
        layer: 'texture',
        url: template.textureOverlayUrl,
        operation: operationFor(template.textureBlendMode),
        opacity: template.textureOpacity ?? 1,
      });
    }
  }

  // Pinned above the ordered stack, still inside the mask.
  if (template.shadowOverlayUrl) {
    steps.push({
      kind: 'image',
      layer: 'shadow',
      url: template.shadowOverlayUrl,
      operation: operationFor(template.shadowBlendMode),
      opacity: template.shadowOpacity ?? 1,
    });
  }
  return steps;
}

/**
 * Where the print-area export lands on the mockup, in mockup pixels: the
 * template's own rectangle (templateRect, R7).
 */
export function designRect(template: UIProductMockupTemplate): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return { ...template.printAreaRect };
}

const LOAD_TIMEOUT_MS = 15_000;
/** Longest edge of the composed image: sharp on a phone, light to send. */
const DEFAULT_EDGE = 1200;

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timeout = setTimeout(() => {
      image.onload = null;
      image.onerror = null;
      reject(new Error(`image timed out: ${url.slice(0, 80)}`));
    }, LOAD_TIMEOUT_MS);
    // The canvas is read back, so every layer has to be CORS-clean.
    if (!url.startsWith('data:') && !url.startsWith('blob:')) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => {
      clearTimeout(timeout);
      resolve(image);
    };
    image.onerror = () => {
      clearTimeout(timeout);
      reject(new Error(`image failed to load: ${url.slice(0, 80)}`));
    };
    image.src = url;
  });
}

function makeCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  return { canvas, ctx };
}

/** `object-contain`: the whole image, centred, never cropped. */
function drawContained(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number
) {
  const scale = Math.min(
    width / image.naturalWidth,
    height / image.naturalHeight
  );
  const w = image.naturalWidth * scale;
  const h = image.naturalHeight * scale;
  ctx.drawImage(image, (width - w) / 2, (height - h) / 2, w, h);
}

/** Cut a canvas down to a mask's silhouette. */
async function applyMask(
  ctx: CanvasRenderingContext2D,
  maskUrl: string,
  width: number,
  height: number
) {
  const mask = await loadImage(await toAlphaMaskUrl(maskUrl));
  ctx.globalCompositeOperation = 'destination-in';
  ctx.globalAlpha = 1;
  drawContained(ctx, mask, width, height);
  ctx.globalCompositeOperation = 'source-over';
}

export async function composeMockupImage(
  input: MockupCompositeInput
): Promise<Blob> {
  const { template, backgroundColor } = input;
  const mockupWidth = template.mockupWidthPx;
  const mockupHeight = template.mockupHeightPx;
  // Mockup pixels are layout units (500 for most templates); the layer files
  // are far larger. Drawing at the target size uses what they have.
  const scale =
    (input.maxEdge ?? DEFAULT_EDGE) / Math.max(mockupWidth, mockupHeight);
  const width = Math.round(mockupWidth * scale);
  const height = Math.round(mockupHeight * scale);

  const steps = planMockupLayers(input);
  // All layers at once; the order only matters when they are drawn.
  const images = await Promise.all(steps.map((step) => loadImage(step.url)));

  // The masked product stack on a canvas of its own.
  const product = makeCanvas(width, height);
  if (template.cutoutMaskUrl) {
    product.ctx.fillStyle = backgroundColor;
    product.ctx.fillRect(0, 0, width, height);
  }
  const rect = designRect(template);
  steps.forEach((step, index) => {
    const image = images[index] as HTMLImageElement;
    if (step.kind === 'design') {
      product.ctx.globalCompositeOperation = 'source-over';
      product.ctx.globalAlpha = 1;
      product.ctx.drawImage(
        image,
        rect.x * scale,
        rect.y * scale,
        rect.width * scale,
        rect.height * scale
      );
      return;
    }
    product.ctx.globalCompositeOperation = step.operation;
    product.ctx.globalAlpha = step.opacity;
    drawContained(product.ctx, image, width, height);
  });
  product.ctx.globalCompositeOperation = 'source-over';
  product.ctx.globalAlpha = 1;
  if (template.cutoutMaskUrl) {
    await applyMask(product.ctx, template.cutoutMaskUrl, width, height);
  }

  const result = makeCanvas(width, height);
  result.ctx.fillStyle = input.frameBackground ?? '#FFFFFF';
  result.ctx.fillRect(0, 0, width, height);
  result.ctx.drawImage(product.canvas, 0, 0);

  // Cords and the like: garment colour with the shading map over it, cut to
  // the cord mask, in front of the design.
  if (template.toplayerMaskUrl) {
    const cords = makeCanvas(width, height);
    cords.ctx.fillStyle = backgroundColor;
    cords.ctx.fillRect(0, 0, width, height);
    cords.ctx.globalAlpha = template.baseOpacity ?? 1;
    drawContained(cords.ctx, await loadImage(template.imageUrl), width, height);
    await applyMask(cords.ctx, template.toplayerMaskUrl, width, height);
    result.ctx.drawImage(cords.canvas, 0, 0);
    cords.canvas.width = 0;
    cords.canvas.height = 0;
  }

  // Frames, clips, hangers: above everything, never masked.
  if (template.topOverlayUrl) {
    drawContained(
      result.ctx,
      await loadImage(template.topOverlayUrl),
      width,
      height
    );
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    result.canvas.toBlob(resolve, 'image/png')
  );
  // Hand the memory back at once; iOS counts canvases per page.
  for (const { canvas } of [product, result]) {
    canvas.width = 0;
    canvas.height = 0;
  }
  if (!blob) throw new Error('mockup could not be encoded');
  return blob;
}
